import { z } from 'zod';
import { safeParseRichDoc, type RichDoc } from './doc';
import { safeHref } from './links';
import { plainText } from './text';

/**
 * Sayfa bölümleri (ana sayfa, kurumsal sayfa, kampanya sayfası). Her bölüm türünün alanları sabittir;
 * serbest HTML/CSS yoktur. Vitrin her türü kendi bileşeniyle çizer.
 */

/** Zengin metin alanı: zod şemasına gömülü belge doğrulaması (hata kodu = ileti, yol = belge içi konum). */
export const richDocSchema = z.unknown().transform((value, ctx): RichDoc => {
  const r = safeParseRichDoc(value);
  if (r.ok) return r.doc;
  for (const issue of r.issues) ctx.addIssue({ code: 'custom', message: issue.code, path: issue.path });
  return z.NEVER;
});

export const hrefSchema = z.string().transform((value, ctx) => {
  const href = safeHref(value);
  if (href) return href;
  ctx.addIssue({ code: 'custom', message: 'content_invalid_link' });
  return z.NEVER;
});

export const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const optText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((v) => v || null);
const fileId = z.uuid();

/** Banner yerleşimleri (F4-05). Sayfa bölümü yalnızca sayfa içi yerleşimleri gösterebilir. */
export const BANNER_PLACEMENTS = [
  'home_hero',
  'home_secondary',
  'category_top',
  'announcement',
  'popup',
] as const;
export type BannerPlacement = (typeof BANNER_PLACEMENTS)[number];

const base = {
  id: z.string().regex(/^[a-z0-9-]{1,40}$/, 'invalid_section_id'),
  hidden: z.boolean().optional().default(false),
};

const image = z
  .object({ fileId, alt: z.string().trim().min(1, 'content_image_alt_required').max(300) })
  .nullable()
  .optional()
  .transform((v) => v ?? null);

export const SectionSchema = z.discriminatedUnion('type', [
  z.object({
    ...base,
    type: z.literal('hero'),
    heading: z.string().trim().min(1).max(120),
    text: optText(300),
    image,
    cta: z
      .object({ label: z.string().trim().min(1).max(40), href: hrefSchema })
      .nullable()
      .optional()
      .transform((v) => v ?? null),
  }),
  z.object({ ...base, type: z.literal('richText'), doc: richDocSchema }),
  z.object({
    ...base,
    type: z.literal('imageText'),
    heading: optText(120),
    image: z.object({
      fileId: z.uuid('section_image_required'),
      alt: z.string().trim().min(1, 'content_image_alt_required').max(300),
    }),
    doc: richDocSchema,
    imagePosition: z.enum(['start', 'end']).default('start'),
  }),
  z
    .object({
      ...base,
      type: z.literal('productGrid'),
      heading: optText(120),
      /**
       * Ürün kaynağı (F5B-04): en yeniler, kategori, elle seçim, çok satanlar (son 30 günün ödenmiş
       * siparişlerinden), indirimli (fiyat geçmişine göre gerçek indirim), koleksiyon, marka.
       */
      source: z.enum(['newest', 'category', 'products', 'bestsellers', 'discounted', 'collection', 'brand']),
      categoryId: z.uuid('section_category_required').nullable().optional().default(null),
      productIds: z.array(z.uuid()).max(24).optional().default([]),
      collectionId: z.uuid('section_collection_required').nullable().optional().default(null),
      brandId: z.uuid('section_brand_required').nullable().optional().default(null),
      limit: z.number().int().min(2).max(24).default(8),
    })
    .refine((s) => s.source !== 'category' || !!s.categoryId, {
      message: 'section_category_required',
      path: ['categoryId'],
    })
    .refine((s) => s.source !== 'collection' || !!s.collectionId, {
      message: 'section_collection_required',
      path: ['collectionId'],
    })
    .refine((s) => s.source !== 'brand' || !!s.brandId, {
      message: 'section_brand_required',
      path: ['brandId'],
    })
    .refine((s) => s.source !== 'products' || s.productIds.length > 0, {
      message: 'section_products_required',
      path: ['productIds'],
    }),
  z.object({
    ...base,
    type: z.literal('categoryTiles'),
    heading: optText(120),
    categoryIds: z.array(z.uuid()).min(1, 'section_category_required').max(12),
  }),
  z.object({
    ...base,
    type: z.literal('banners'),
    placement: z.enum(['home_hero', 'home_secondary']),
  }),
  z.object({
    ...base,
    type: z.literal('faq'),
    heading: optText(120),
    items: z
      .array(z.object({ question: z.string().trim().min(1).max(300), answer: richDocSchema }))
      .min(1)
      .max(30),
  }),
  z.object({
    ...base,
    type: z.literal('postList'),
    heading: optText(120),
    categoryId: z.uuid().nullable().optional().default(null),
    limit: z.number().int().min(1).max(12).default(3),
  }),
]);

export type Section = z.output<typeof SectionSchema>;
export type SectionInput = z.input<typeof SectionSchema>;
export const SECTION_TYPES = [
  'hero',
  'richText',
  'imageText',
  'productGrid',
  'categoryTiles',
  'banners',
  'faq',
  'postList',
] as const satisfies ReadonlyArray<Section['type']>;

export const SectionsSchema = z
  .array(SectionSchema)
  .max(40)
  .refine((list) => new Set(list.map((s) => s.id)).size === list.length, 'duplicate_section_id');

/** Bölümlerdeki metinler (sağlık beyanı denetimi için alan adıyla). */
export function sectionTexts(sections: Section[]): Record<string, string> {
  const out: Record<string, string> = {};
  sections.forEach((s, i) => {
    const key = `sections.${i}`;
    switch (s.type) {
      case 'hero':
        out[key] = [s.heading, s.text, s.image?.alt, s.cta?.label].filter(Boolean).join('\n');
        break;
      case 'richText':
        out[key] = plainText(s.doc);
        break;
      case 'imageText':
        out[key] = [s.heading, s.image.alt, plainText(s.doc)].filter(Boolean).join('\n');
        break;
      case 'faq':
        out[key] = s.items.map((it) => `${it.question}\n${plainText(it.answer)}`).join('\n');
        break;
      case 'productGrid':
      case 'categoryTiles':
      case 'postList':
        if (s.heading) out[key] = s.heading;
        break;
      case 'banners':
        break;
    }
  });
  return out;
}

/** Bölümlerde kullanılan görsel dosyaları. */
export function sectionFileIds(sections: Section[]): string[] {
  const ids: string[] = [];
  const docIds = (doc: RichDoc) => doc.content.flatMap((b) => (b.type === 'image' ? [b.attrs.fileId] : []));
  for (const s of sections) {
    if (s.type === 'hero' && s.image) ids.push(s.image.fileId);
    if (s.type === 'imageText') ids.push(s.image.fileId, ...docIds(s.doc));
    if (s.type === 'richText') ids.push(...docIds(s.doc));
    if (s.type === 'faq') for (const it of s.items) ids.push(...docIds(it.answer));
  }
  return [...new Set(ids)];
}
